import { MoreVertical, ClipboardPlus, Pencil, CircleOff } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@predoc/cow';
import { useNavigate } from 'react-router';
import { OPEN_STATUSES } from '../../constants';
import { ExistingPatient } from '../../types';
import { CancelOpenRequestsDialog } from '../common';
import { Permission, useHasPermission } from '@/utils/roles';

export const PatientActionDropdown = ({
  patient,
  onEditPatient,
}: {
  patient: ExistingPatient;
  onEditPatient: (patient: ExistingPatient) => void;
}) => {
  const navigate = useNavigate();
  const { hasPermission: canRequestWorkflow } = useHasPermission(
    Permission.REQUEST_WORKFLOW,
  );
  const { hasPermission: canEditPatients } = useHasPermission(
    Permission.EDIT_PATIENTS,
  );

  const hasOpenRequests = OPEN_STATUSES.some(
    (status) => (patient.organizationStatus?.[status] ?? 0) > 0,
  );

  const showNewRequest = canRequestWorkflow;
  const showEditDetails = canEditPatients;
  const showCancelRequests = canRequestWorkflow;

  // If no actions are available, hide the dropdown
  if (!showNewRequest && !showEditDetails && !showCancelRequests) {
    return null;
  }

  const handleNewRequest = () => {
    navigate(`/patients/request?patientId=${patient.id}`);
  };

  const handleEditPatientDetails = () => {
    onEditPatient(patient);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Patient actions">
          <MoreVertical className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="bottom" align="end">
        {showNewRequest ? (
          <DropdownMenuItem onClick={handleNewRequest}>
            <ClipboardPlus className="size-4" />
            New Request
          </DropdownMenuItem>
        ) : null}
        {showEditDetails ? (
          <DropdownMenuItem onClick={handleEditPatientDetails}>
            <Pencil className="size-4" />
            Edit Patient Details
          </DropdownMenuItem>
        ) : null}
        {showCancelRequests ? (
          <>
            {showNewRequest || showEditDetails ? (
              <DropdownMenuSeparator />
            ) : null}
            <CancelOpenRequestsDialog patientId={patient.id}>
              <DropdownMenuItem
                className="text-destructive"
                disabled={!hasOpenRequests}
                onSelect={(e: Event) => e.preventDefault()} // To prevent the dropdown from closing and unmount the dialog
              >
                <CircleOff className="text-destructive size-4" />
                Cancel Open Requests
              </DropdownMenuItem>
            </CancelOpenRequestsDialog>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
